// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useMemo, useReducer } from 'react';
import { Platform } from 'react-native';
import { log } from '../log/log';
import type { ImageSource } from '../imageeditor';
import type { ThreadIdentifier } from '../threads';
import type { UserId } from '../profile';

/**
 * Cycle 16 Wave 3 WEB-WIRE-B — `ChatRoomShell` view model.
 *
 * Owns the overlay state machine that backs the chat surface's right-pane
 * thread panel and its modal stack. Pure presentation state — never touches
 * the SDK, `MessagingClient`, `PingStorage`, `PingTransport`, or any
 * identity material. Held by `ChatRoomShell` (or by an outer host that
 * passes the resolved handlers back in) — never by a leaf row.
 *
 * Sensitive payloads (`message.body`, `poll.question`, `threadParent.previewText`,
 * `userProfile.bio`, status text, location coords, image bytes) never enter
 * the reducer. Only opaque identifiers + the editor source array (Blob handles
 * the host owns) flow through it.
 */

/**
 * Discrete overlay kinds the shell can route. The thread panel sits in the
 * right pane and is not "modal" in the React sense, but is tracked so a
 * host that needs to swap to a full-screen sheet on narrow viewports can
 * observe `threadActive`.
 */
export type ChatRoomShellOverlayKind =
  | 'pollComposer'
  | 'meetingComposer'
  | 'shareLocation'
  | 'setStatus'
  | 'settings'
  | 'imageEditor'
  | 'userProfile'
  | 'miniProfile'
  | 'thread';

/** Anchor ref carried by mini-profile invocations. Opaque to the reducer. */
export type ChatRoomMiniProfileAnchorRef = React.RefObject<unknown>;

export interface ChatRoomShellState {
  /**
   * Modal stack — single-active policy. The thread panel may coexist with
   * a modal entry; it never enters this array. `userProfile` and
   * `miniProfile` are mutually exclusive (opening one clears the other).
   */
  readonly activeOverlays: ReadonlyArray<ChatRoomShellOverlayKind>;
  readonly threadIdentifier: ThreadIdentifier | null;
  readonly miniProfileUserId: UserId | null;
  readonly miniProfileAnchor: ChatRoomMiniProfileAnchorRef | null;
  readonly userProfileUserId: UserId | null;
  /** Display hints captured at open time (the chat already knows the
   *  tapped user's name + avatar). Lets the profile panel render real
   *  identity immediately instead of an empty "Member" placeholder while
   *  the directory round-trip resolves. */
  readonly userProfileHint: UserProfileHint | null;
  readonly imageEditorSources: readonly ImageSource[];
}

export interface UserProfileHint {
  readonly displayName?: string | undefined;
  readonly avatarUri?: string | undefined;
}

export interface ChatRoomShellOverlayHandlers {
  readonly openPollComposer: () => void;
  /** Opens the meeting composer (Event attach entry) → `com.ping.meeting`.
   *  Optional — hosts that haven't wired meetings leave it undefined. */
  readonly openMeetingComposer?: (() => void) | undefined;
  readonly openShareLocation: () => void;
  /** Opens the game-kind picker (coinflip/dice/rps/chess/blackjack/
   *  racing) which emits a GAME_CREATE envelope. Optional — hosts that
   *  haven't wired games yet leave it undefined. */
  readonly openGamePicker?: (() => void) | undefined;
  readonly openSetStatus: () => void;
  readonly openSettings: () => void;
  readonly openImageEditor: (sources: readonly ImageSource[]) => void;
  readonly openUserProfile: (userId: UserId, hint?: UserProfileHint) => void;
  readonly openMiniProfile: (userId: UserId, anchorRef: ChatRoomMiniProfileAnchorRef) => void;
  readonly openThread: (identifier: ThreadIdentifier) => void;
  readonly closeOverlay: (kind: ChatRoomShellOverlayKind) => void;
  readonly closeAllOverlays: () => void;
}

export interface ChatRoomShellViewModel {
  readonly state: ChatRoomShellState;
  readonly handlers: ChatRoomShellOverlayHandlers;
  readonly threadActive: boolean;
}

type Action =
  | { readonly type: 'openPollComposer' }
  | { readonly type: 'openMeetingComposer' }
  | { readonly type: 'openShareLocation' }
  | { readonly type: 'openSetStatus' }
  | { readonly type: 'openSettings' }
  | { readonly type: 'openImageEditor'; readonly sources: readonly ImageSource[] }
  | { readonly type: 'openUserProfile'; readonly userId: UserId; readonly hint?: UserProfileHint }
  | {
      readonly type: 'openMiniProfile';
      readonly userId: UserId;
      readonly anchorRef: ChatRoomMiniProfileAnchorRef;
    }
  | { readonly type: 'openThread'; readonly identifier: ThreadIdentifier }
  | { readonly type: 'closeOverlay'; readonly kind: ChatRoomShellOverlayKind }
  | { readonly type: 'closeAllOverlays' };

const INITIAL_STATE: ChatRoomShellState = {
  activeOverlays: [],
  threadIdentifier: null,
  miniProfileUserId: null,
  miniProfileAnchor: null,
  userProfileUserId: null,
  userProfileHint: null,
  imageEditorSources: [],
};

const MODAL_OVERLAY_KINDS: ReadonlyArray<ChatRoomShellOverlayKind> = [
  'pollComposer',
  'meetingComposer',
  'shareLocation',
  'setStatus',
  'settings',
  'imageEditor',
  'userProfile',
  'miniProfile',
];

function pushModal(
  state: ChatRoomShellState,
  kind: ChatRoomShellOverlayKind,
): ReadonlyArray<ChatRoomShellOverlayKind> {
  // Single-active policy: open* replaces any prior modal stack entry but
  // leaves the thread panel untouched if it is open (it never enters this
  // array). Callers that want to clear the thread call `closeOverlay('thread')`
  // explicitly.
  const carry = state.activeOverlays.filter((k) => k === 'thread');
  return [...carry, kind];
}

function removeOverlay(
  state: ChatRoomShellState,
  kind: ChatRoomShellOverlayKind,
): ReadonlyArray<ChatRoomShellOverlayKind> {
  return state.activeOverlays.filter((k) => k !== kind);
}

function reducer(state: ChatRoomShellState, action: Action): ChatRoomShellState {
  switch (action.type) {
    case 'openPollComposer':
      return { ...state, activeOverlays: pushModal(state, 'pollComposer') };
    case 'openMeetingComposer':
      return { ...state, activeOverlays: pushModal(state, 'meetingComposer') };
    case 'openShareLocation':
      return { ...state, activeOverlays: pushModal(state, 'shareLocation') };
    case 'openSetStatus':
      return { ...state, activeOverlays: pushModal(state, 'setStatus') };
    case 'openSettings':
      return { ...state, activeOverlays: pushModal(state, 'settings') };
    case 'openImageEditor':
      // Web hosts upload on file pick; never push the editor overlay.
      if (Platform.OS === 'web') {
        return state;
      }
      return {
        ...state,
        activeOverlays: pushModal(state, 'imageEditor'),
        imageEditorSources: action.sources,
      };
    case 'openUserProfile':
      return {
        ...state,
        activeOverlays: pushModal(state, 'userProfile'),
        userProfileUserId: action.userId,
        userProfileHint: action.hint ?? null,
        // A user-profile overlay supersedes any pending mini-profile.
        miniProfileUserId: null,
        miniProfileAnchor: null,
      };
    case 'openMiniProfile':
      return {
        ...state,
        activeOverlays: pushModal(state, 'miniProfile'),
        miniProfileUserId: action.userId,
        miniProfileAnchor: action.anchorRef,
      };
    case 'openThread': {
      const carry = state.activeOverlays.filter((k) => k !== 'thread');
      return {
        ...state,
        activeOverlays: [...carry, 'thread'],
        threadIdentifier: action.identifier,
      };
    }
    case 'closeOverlay': {
      const next: ChatRoomShellState = {
        ...state,
        activeOverlays: removeOverlay(state, action.kind),
      };
      if (action.kind === 'thread') {
        return { ...next, threadIdentifier: null };
      }
      if (action.kind === 'imageEditor') {
        return { ...next, imageEditorSources: [] };
      }
      if (action.kind === 'miniProfile') {
        return { ...next, miniProfileUserId: null, miniProfileAnchor: null };
      }
      if (action.kind === 'userProfile') {
        return { ...next, userProfileUserId: null, userProfileHint: null };
      }
      return next;
    }
    case 'closeAllOverlays':
      return INITIAL_STATE;
  }
}

/**
 * Pure helper exported so hosts (or tests) can derive the boolean
 * "thread panel visible" condition without depending on the hook
 * shape.
 */
export function isThreadActive(state: ChatRoomShellState): boolean {
  return state.threadIdentifier !== null;
}

/**
 * Pure helper used by hosts that need a count of active modal overlays
 * for keyboard / focus management.
 */
export function activeModalCount(state: ChatRoomShellState): number {
  return state.activeOverlays.filter((k) => MODAL_OVERLAY_KINDS.includes(k)).length;
}

/**
 * Returns the topmost modal overlay (excluding `thread`). `null` when the
 * stack is empty / only `thread` is active.
 */
export function topModalOverlay(
  state: ChatRoomShellState,
): ChatRoomShellOverlayKind | null {
  for (let i = state.activeOverlays.length - 1; i >= 0; i -= 1) {
    const kind = state.activeOverlays[i];
    if (kind !== undefined && kind !== 'thread') {
      return kind;
    }
  }
  return null;
}

function shortUserPrefix(userId: UserId): string {
  return userId.length <= 6 ? userId : userId.slice(0, 6);
}

function shortThreadPrefix(identifier: ThreadIdentifier): string {
  return identifier.length <= 6 ? identifier : identifier.slice(0, 6);
}

export function useChatRoomShellViewModel(): ChatRoomShellViewModel {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const handlers = useMemo<ChatRoomShellOverlayHandlers>(
    () => ({
      openPollComposer: () => {
        log.debug('shell.overlay.open', { kind: 'pollComposer' });
        dispatch({ type: 'openPollComposer' });
      },
      openMeetingComposer: () => {
        log.debug('shell.overlay.open', { kind: 'meetingComposer' });
        dispatch({ type: 'openMeetingComposer' });
      },
      openShareLocation: () => {
        log.debug('shell.overlay.open', { kind: 'shareLocation' });
        dispatch({ type: 'openShareLocation' });
      },
      openSetStatus: () => {
        log.debug('shell.overlay.open', { kind: 'setStatus' });
        dispatch({ type: 'openSetStatus' });
      },
      openSettings: () => {
        log.debug('shell.overlay.open', { kind: 'settings' });
        dispatch({ type: 'openSettings' });
      },
      openImageEditor: (sources) => {
        // Web uploads directly from the host file picker — never show
        // the crop/draw modal (see apps/web WebChatRoom).
        if (Platform.OS === 'web') {
          log.debug('shell.imageEditor.skipped.web', { count: sources.length });
          return;
        }
        log.debug('shell.overlay.open', { kind: 'imageEditor', count: sources.length });
        dispatch({ type: 'openImageEditor', sources });
      },
      openUserProfile: (userId, hint) => {
        log.debug('shell.overlay.open', {
          kind: 'userProfile',
          uid: shortUserPrefix(userId),
        });
        dispatch({ type: 'openUserProfile', userId, ...(hint !== undefined ? { hint } : {}) });
      },
      openMiniProfile: (userId, anchorRef) => {
        log.debug('shell.overlay.open', {
          kind: 'miniProfile',
          uid: shortUserPrefix(userId),
        });
        dispatch({ type: 'openMiniProfile', userId, anchorRef });
      },
      openThread: (identifier) => {
        log.debug('shell.overlay.open', {
          kind: 'thread',
          tid: shortThreadPrefix(identifier),
        });
        dispatch({ type: 'openThread', identifier });
      },
      closeOverlay: (kind) => {
        log.debug('shell.overlay.close', { kind });
        dispatch({ type: 'closeOverlay', kind });
      },
      closeAllOverlays: () => {
        log.debug('shell.overlay.closeAll');
        dispatch({ type: 'closeAllOverlays' });
      },
    }),
    [],
  );

  return useMemo<ChatRoomShellViewModel>(
    () => ({ state, handlers, threadActive: isThreadActive(state) }),
    [state, handlers],
  );
}
